import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as Collapsible from '@radix-ui/react-collapsible';
import { PiCaretUpBold } from 'react-icons/pi';
import clsx from 'clsx';
import { SolutionGroupId, type SolutionGroupStatus } from '@shared/types/entities/solution-group';
import { WorkerStatusColor } from '@shared/types/entities/worker';
import {
  truncateBalance,
  getPendingUnsubscriptionStatus,
  getLiquidTokenSymbol,
  getNativeTokenSymbol,
} from '@ewf/lib/utils';
import { GenericChip } from '@ewf/components/Chips/GenericChip';
import { CircularChartSingular } from '@ewf/components/Charts/Circular/CircularChartSingular';
import { Tooltip } from '@ewf/components/Tooltip';
import solutionGroupIcon from '@ewf/assets/icons/solution-group.svg';
import claimRewardsIcon from '@ewf/assets/icons/claim-rewards.svg';
import topupIcon from '@ewf/assets/icons/top-up.svg';
import manageSubscriptionIcon from '@ewf/assets/icons/gear-gradient.svg';
import CalendarGradient from '@ewf/assets/icons/calendar-gradient.svg';
import NextVotingIcon from '@ewf/assets/icons/next-voting.svg';
import questionMarkIcon from '@ewf/assets/icons/question.svg';
import {
  EnableWorkerButton,
  PauseWorkerButton,
  ResumeWorkerButton,
  NoWorkerButton,
} from './WorkerButton';
import { ScheduledUnsubscriptionChip } from '@ewf/components/Chips/ScheduledUnsubscriptionChip';
import { AutoInstallationScheduledChip } from '@ewf/components/Chips/AutoInstallationScheduledChip';
import { SyncingChip } from '@ewf/components/Chips/SyncingChip';
import { useWorkerExecutionStore } from '@ewf/stores/worker-execution';
import { useApiServiceStore } from '@ewf/stores/api';
import { GENERIC_CHIP_COLOR, WORKER_STATUS, WORKER_STATUS_COLOR } from '@shared/types/enums';
import { isElectron } from '@shared/utils/is-electron';
import { displayBalance } from '@ewf/utils/balance-helpers';

export const GalaxySubscriptionHorizontal = (
  solutionGroup: GalaxySubscriptionHorizontalProps,
): JSX.Element => {
  const workerApi = useApiServiceStore.getState().api;
  const {
    id,
    name,
    stakeAmount,
    floatingStakeAmount,
    isVotingEnabled,
    workerAddress,
    parentData,
    isStartVoteNextPeriod,
    isExpired,
    onOpenTopupStaking,
    isPendingUnsubscribe,
    unsubscriptionDate,
    withdrawalDelay,
    onOpenClaimReward,
    onOpenManageVoting,
    onOpenManageWorker,
    pendingRewards,
    isExpanded,
    onToggleCollapse,
    isKycEnabled,
  } = solutionGroup;
  const { workerLinkedToEwx, ewtusd = 0 } = parentData;
  const [currentRewardPeriod, setCurrentRewardPeriod] = useState<number | null>(null);
  const [currentVotesCasted, setCurrentVotesCasted] = useState<number | null>(null);
  const [isRewardDataLoading, setIsRewardDataLoading] = useState(false);
  const workerExecution = useWorkerExecutionStore((state) => state.status);
  const pendingUnsubscriptionStatus = getPendingUnsubscriptionStatus(isPendingUnsubscribe);
  const truncatedPendingRewards = truncateBalance(parseFloat(pendingRewards.amount));

  const chipStatus: SolutionGroupStatus = isExpired ? 'expired' : 'running';
  const chips: Record<SolutionGroupStatus, JSX.Element | null> = {
    scheduled: <GenericChip label="Scheduled" color={GENERIC_CHIP_COLOR.LightPink} />,
    running:
      workerExecution === 'local' ? (
        <GenericChip
          label={solutionGroup.actualStatus || WORKER_STATUS.NotRunning}
          color={WORKER_STATUS_COLOR[solutionGroup.actualStatus || WORKER_STATUS.NotRunning]}
        />
      ) : null,
    expired: <GenericChip label="Expired" />,
  };

  const updateVotingInfo = useCallback(async () => {
    try {
      const [{ votes }, rewardPeriod] = await Promise.all([
        workerApi.getCurrentVoteAndRewardPeriod(id),
        workerApi.getCurrentRewardPeriod(),
      ]);
      setCurrentRewardPeriod(rewardPeriod);
      setCurrentVotesCasted(votes);
    } catch (e) {
      console.error('Error updating voting info', e);
    }
  }, [id, workerApi]);

  useEffect(() => {
    try {
      setIsRewardDataLoading(true);
      updateVotingInfo();
    } catch (e) {
      console.error(e);
    } finally {
      setIsRewardDataLoading(false);
    }

    const interval = setInterval(() => {
      updateVotingInfo();
    }, 1000 * 30); // Every 30 seconds

    return () => clearInterval(interval);
  }, [updateVotingInfo]);

  return (
    <div className="w-full max-w-[832px]">
      <Collapsible.Root open={isExpanded} onOpenChange={onToggleCollapse}>
        {/* Header */}
        <div className="mb-1 flex w-full gap-1">
          <div className="flex flex-grow flex-row items-center gap-4 rounded-sm rounded-tl-lg bg-gray-90 p-4 shadow-sm">
            <div className="flex h-auto w-fit flex-shrink-0 items-center rounded-full bg-brand/10 p-3">
              <img src={solutionGroupIcon} width={16} height={16} alt="GalaxySubscriptionIcon" />
            </div>
            <div className="mr-4 flex items-center gap-4">
              <p className="flex-shrink-0 whitespace-nowrap text-lg" title={id}>
                {name}
              </p>
              <div className="flex flex-wrap gap-4">
                {workerExecution === 'local' ? chips[chipStatus] : null}
                {workerExecution === 'local' &&
                solutionGroup &&
                solutionGroup.actionStatus !== 'NoAction' &&
                solutionGroup.actualStatus !== solutionGroup.actionStatus ? (
                  solutionGroup?.actionStatus === 'Installing' && isStartVoteNextPeriod ? (
                    <AutoInstallationScheduledChip />
                  ) : (
                    <GenericChip
                      label={solutionGroup.actionStatus || WORKER_STATUS.NotRunning}
                      color={
                        WorkerStatusColor[solutionGroup.actionStatus || WORKER_STATUS.NotRunning]
                      }
                    />
                  )
                ) : null}
              </div>

              {pendingUnsubscriptionStatus === 'syncing' ? (
                <SyncingChip />
              ) : pendingUnsubscriptionStatus === 'pending' && unsubscriptionDate ? (
                <ScheduledUnsubscriptionChip
                  unsubscriptionDate={unsubscriptionDate}
                  withdrawalDelay={withdrawalDelay}
                />
              ) : null}
            </div>
          </div>
          <Collapsible.Trigger className="flex items-center justify-center rounded-sm rounded-tr-lg bg-gray-90 p-4 shadow-sm">
            <PiCaretUpBold
              className={clsx({
                'rotate-180 transform': !isExpanded,
                'transition-transform duration-300': true,
              })}
            />
          </Collapsible.Trigger>
        </div>
        {/* Body */}
        <Collapsible.Content>
          <div className="space-y-4 rounded-b-lg rounded-t-sm bg-gray-90 p-4 shadow-sm">
            <div className="grid grid-cols-5 gap-4 ">
              {/* Stake amount */}
              <div className="col-span-4 flex h-full w-full flex-col items-center justify-center rounded-md border-2 border-transparent p-3 shadow-darker gradient-border-dark-with-gray-85">
                <p className="mb-2 text-sm text-font-subtler">
                  {getLiquidTokenSymbol()} Subscription Amount
                </p>
                <div className="flex h-full w-full flex-col items-center justify-center rounded-md px-6 py-3 text-center bg-dark-gradient">
                  <h2
                    className="mb-1 max-w-[200px] truncate font-primary-bold text-xl text-gradient"
                    aria-label="stake-amount"
                  >
                    {displayBalance(truncateBalance(stakeAmount))}
                    {floatingStakeAmount && floatingStakeAmount !== '0' ? (
                      <>
                        <span className="text-base"> + {floatingStakeAmount}</span>
                        <Tooltip
                          side="bottom"
                          // className="border-none"
                          content={
                            <div className="max-w-[300px] p-2">
                              This amount will be added to the total subscription in the next reward
                              period.
                            </div>
                          }
                        >
                          <div className="ml-2 flex h-[16px] w-[16px] items-center justify-center rounded-full bg-gray-100">
                            <img src={questionMarkIcon} width={11} height={11} alt="more info" />
                          </div>
                        </Tooltip>
                      </>
                    ) : (
                      ''
                    )}
                  </h2>
                  <p className="text-font-subtler">
                    {displayBalance(truncateBalance(+stakeAmount * ewtusd))} USD
                  </p>
                </div>
              </div>
              {/* Top-up stake */}
              <button
                onClick={onOpenTopupStaking}
                title="Top-up subscription"
                className="group col-span-1 flex flex-col items-center justify-center gap-3 rounded-md border-2 border-transparent p-3 shadow-darker transition delay-75 ease-in-out gradient-border-dark-with-gray-85 hover:border-brand/60"
              >
                <p className="my-1 text-sm text-font-subtler">Top-up</p>
                <div className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-dark-gradient">
                  <div className="group-after:bg-button-gray-gradient absolute flex h-[45px] w-[45px] items-center justify-center rounded-full bg-gray-80 shadow-sm group-active:bg-button-gray-gradient">
                    <img src={topupIcon} width={24} height={24} alt="topupIcon" />
                  </div>
                </div>
              </button>
              {/* Pending rewards */}
              <div className="col-span-4 flex h-full w-full flex-col items-center justify-center rounded-md border-2 border-transparent p-3 shadow-darker gradient-border-dark-with-gray-85">
                <p className="mb-2 text-sm text-font-subtler">Pending Rewards</p>
                <div className="flex h-full w-full flex-col items-center justify-center rounded-md px-6 py-3 text-center bg-dark-gradient">
                  <h2
                    className="mb-1 max-w-[200px] truncate font-primary-bold text-xl text-gradient"
                    aria-label="pending-rewards-amount"
                  >
                    {truncatedPendingRewards < 0.0001 && truncatedPendingRewards != 0
                      ? '< 0.0001'
                      : truncatedPendingRewards}
                  </h2>
                  <p className="text-sm text-font-subtler">
                    {pendingRewards.isNative ? getNativeTokenSymbol() : pendingRewards.symbol}
                  </p>
                </div>
              </div>
              {/* Claim rewards */}
              <button
                onClick={onOpenClaimReward}
                disabled={pendingRewards.amount === '0'}
                title={pendingRewards.amount === '0' ? 'No rewards to claim' : 'Claim Rewards'}
                className="group col-span-1 flex flex-col items-center justify-center gap-3 rounded-md border-2 border-transparent p-3 shadow-darker transition delay-75 ease-in-out gradient-border-dark-with-gray-85 enabled:hover:border-brand/60 disabled:opacity-40 disabled:hover:border-transparent"
              >
                <p className="my-1 text-sm text-font-subtler">Claim Rewards</p>
                <div className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-dark-gradient">
                  <div
                    className={clsx({
                      'group-after:bg-button-gray-gradient absolute flex h-[45px] w-[45px] items-center justify-center rounded-full bg-gray-80 shadow-sm':
                        true,
                      'group-active:bg-button-gray-gradient': pendingRewards.amount !== '0',
                    })}
                  >
                    <img src={claimRewardsIcon} width={32} height={32} alt="claimRewardsIcon" />
                  </div>
                </div>
              </button>
            </div>
            <div className="grid grid-cols-10 gap-4 rounded-b-lg rounded-t-sm bg-gray-90">
              {/* Current reward period */}
              <div
                className={clsx({
                  'flex flex-col items-center justify-center rounded-md border-2 border-transparent bg-gray-90 p-2 shadow-sm gradient-border-dark-with-gray-85':
                    true,
                  'col-span-3': workerExecution === 'local',
                  'col-span-4': workerExecution === 'remote',
                })}
              >
                <p className="my-1 text-sm text-font-subtler">Current Reward Period</p>
                <div className="relative mt-3 flex h-[64px] w-[64px] items-center justify-center rounded-full text-center bg-dark-gradient">
                  <img src={CalendarGradient} width={35} alt="CalendarGradient" />
                  <div className="absolute bottom-2 right-2 flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-gray-70 text-base bg-dark-gradient">
                    {isRewardDataLoading ? '-' : currentRewardPeriod}
                  </div>
                </div>
              </div>
              {/* Votes casted */}
              <div
                className={clsx({
                  'col-span-3': workerExecution === 'local',
                  'col-span-4': workerExecution === 'remote',
                })}
              >
                {/* Votes casted graph */}
                <div
                  className={clsx({
                    'flex h-full flex-col items-center justify-center rounded-md border-2 border-transparent bg-gray-90 p-3 shadow-sm gradient-border-dark-with-gray-85':
                      true,
                    hidden: isStartVoteNextPeriod,
                  })}
                >
                  <p
                    className={clsx({
                      'my-1 animate-pulse text-sm text-font-subtler': true,
                      hidden: isStartVoteNextPeriod !== undefined,
                    })}
                  >
                    Loading...
                  </p>
                  <div
                    className={clsx({
                      'relative flex flex-col items-center justify-center': true,
                      hidden: isStartVoteNextPeriod !== false,
                    })}
                  >
                    <p className="my-1 text-sm text-font-subtler">Votes cast in this period</p>
                    {isKycEnabled && (
                      <div className="absolute top-10 right-4">
                        <Tooltip
                          side="top"
                          content={
                            <div className="max-w-[300px] p-2">
                              If the votes count is always displayed 0, this worker may not be eligible to submit votes for this solution group.
                            </div>
                          }
                        >
                          <div className="flex h-[16px] w-[16px] justify-center rounded-full bg-gray-95">
                            <img src={questionMarkIcon} width={11} height={11} alt="more info" />
                          </div>
                        </Tooltip>
                      </div>
                    )}
                    <div className="mt-3 flex h-[64px] w-[64px] items-center justify-center rounded-full text-center bg-dark-gradient">
                      <CircularChartSingular
                        value={isRewardDataLoading || !currentVotesCasted ? 0 : currentVotesCasted}
                      />
                    </div>
                  </div>
                </div>
                {/* Start to vote on next period */}
                <div
                  className={clsx({
                    'flex flex-col gap-1': true,
                    hidden: !isStartVoteNextPeriod,
                  })}
                >
                  <div
                    style={{
                      borderTopWidth: '1px',
                    }}
                    className="flex h-full flex-col items-center justify-center rounded-b-sm rounded-t-md border-transparent bg-gray-90 p-3 shadow-sm gradient-border-primary-with-gray-85"
                  >
                    <p className="text-center text-sm text-font-subtler">
                      Node will start voting in the next reward period
                    </p>
                  </div>
                  <div
                    style={{
                      borderBottomRightRadius: '8px',
                      borderBottomLeftRadius: '8px',
                    }}
                    className="flex flex-col items-center justify-center rounded-t-sm border-2 border-transparent bg-gray-90 p-3 shadow-sm gradient-border-dark-with-gray-85"
                  >
                    <div className="relative flex h-[64px] w-[64px] items-center justify-center rounded-full text-center bg-dark-gradient">
                      <img src={NextVotingIcon} width={35} alt="NextVotingIcon" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Worker button */}
              <div
                className={clsx({
                  'col-span-2': true,
                  hidden: workerExecution === 'remote',
                })}
              >
                <WorkerButton
                  solutionGroup={solutionGroup}
                  openManageVoting={onOpenManageVoting}
                  openManageWorker={onOpenManageWorker}
                  isVotingEnabled={isVotingEnabled}
                  workerAddress={workerAddress}
                  workerLinkedToEwx={workerLinkedToEwx}
                />
                {/* Manage Subscription */}
              </div>
              <Link
                to={`/dashboard/${id}`}
                title="Manage Subscription"
                className="group col-span-2 flex flex-col items-center justify-center gap-3 rounded-md border-2 border-transparent p-3 shadow-darker transition delay-75 ease-in-out gradient-border-dark-with-gray-85 hover:border-brand/60"
              >
                <p className="my-1 text-center text-sm text-font-subtler">Manage Subscription</p>
                <div className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-dark-gradient">
                  <div className="group-after:bg-button-gray-gradient absolute flex h-[45px] w-[45px] items-center justify-center rounded-full bg-gray-80 shadow-sm group-active:bg-button-gray-gradient">
                    <img
                      src={manageSubscriptionIcon}
                      width={24}
                      height={24}
                      alt="claimRewardsIcon"
                    />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
};

export interface GalaxySubscriptionHorizontalProps {
  id: SolutionGroupId;
  name: string;
  isActive: boolean;
  isExpired: boolean;
  isVotingEnabled: boolean;
  isStartVoteNextPeriod: boolean | undefined;
  status?: string;
  actionStatus?: string | null;
  actualStatus?: string | null;
  stakeAmount: string;
  floatingStakeAmount: string;
  workerAddress: string;
  solutionsCount: number;
  parentData: ParentData;
  onOpenTopupStaking: () => void;
  isPendingUnsubscribe: boolean | null;
  unsubscriptionDate: Date | null;
  withdrawalDelay: number;
  onOpenClaimReward: () => void;
  onOpenManageVoting: () => void;
  onOpenManageWorker: () => void;
  pendingRewards: {
    amount: string;
    symbol: string;
    decimals: number;
    isNative: boolean;
  };
  isExpanded: boolean;
  onToggleCollapse: (isExpanded: boolean) => void;
  isKycEnabled?: boolean;
}

export const WorkerButton = ({
  solutionGroup,
  openManageVoting,
  isVotingEnabled,
  workerAddress,
  workerLinkedToEwx,
  openManageWorker,
}: WorkerButtonProps): JSX.Element => {
  if (!isVotingEnabled || !workerAddress || !workerLinkedToEwx || !solutionGroup.status) {
    if (
      solutionGroup.solutionsCount &&
      !solutionGroup.isExpired &&
      (!solutionGroup.actionStatus ||
        solutionGroup.actionStatus === 'NoAction' ||
        solutionGroup.actionStatus === WORKER_STATUS.DownloadFailed)
    ) {
      return isElectron() ? <EnableWorkerButton onClick={openManageVoting} /> : <NoWorkerButton />;
    } else {
      return <NoWorkerButton />;
    }
  }

  if (isVotingEnabled && workerAddress && workerLinkedToEwx) {
    if (solutionGroup.status === WORKER_STATUS.Paused) {
      return <ResumeWorkerButton onClick={openManageWorker} />;
    }
    if (solutionGroup.status === WORKER_STATUS.Running) {
      return <PauseWorkerButton onClick={openManageWorker} />;
    }
  }

  return <NoWorkerButton />;
};

interface WorkerButtonProps {
  solutionGroup: any;
  openManageVoting: () => void;
  isVotingEnabled: boolean;
  workerAddress: string;
  workerLinkedToEwx: boolean;
  openManageWorker: () => void;
}

export interface ParentData {
  ewtusd?: number;
  workerLinkedToEwx: boolean;
}
